import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, PencilLine, Trash2, Eye, ExternalLink, Search, Star } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  published: "bg-emerald-100 text-emerald-800",
  draft:     "bg-neutral-200 text-neutral-800",
  scheduled: "bg-amber-100 text-amber-800",
  archived:  "bg-neutral-100 text-neutral-500",
};

const AdminBlogList = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/admin/blog/posts", { params });
      setPosts(data.items || []);
    } catch (e) {
      toast.error("Failed to load posts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const del = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${post.id}`);
      toast.success("Post deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <AdminLayout
      title="Blog"
      actions={
        <Button data-testid="new-blog-post-btn" onClick={() => navigate("/admin/blog/new")} className="rounded-none bg-accent hover:bg-accent/90">
          <Plus size={14} className="mr-2" /> New post
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex gap-1 border border-border" data-testid="blog-status-tabs">
            {["", "published", "draft", "scheduled", "archived"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setStatusFilter(s)}
                data-testid={`blog-tab-${s || "all"}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold transition ${statusFilter === s ? "bg-neutral-900 text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
          <div className="flex-1 min-w-[240px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="blog-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search title or slug…"
              className="rounded-none pl-9"
            />
          </div>
        </div>

        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-border">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-widest">Title</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-widest">Status</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-widest">Category</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-widest">Author</th>
                <th className="text-right px-3 py-2 font-semibold text-xs uppercase tracking-widest">Views</th>
                <th className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-widest">Published</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="text-center py-12 text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && posts.length === 0 && (
                <tr><td colSpan="7" className="text-center py-16">
                  <div className="overline text-accent">No posts yet</div>
                  <div className="mt-2 text-muted-foreground">Create your first article to start attracting organic traffic.</div>
                </td></tr>
              )}
              {!loading && posts.map((p) => (
                <tr key={p.id} data-testid={`blog-row-${p.id}`} className="border-b border-border last:border-b-0 hover:bg-neutral-50">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {p.featured && <Star size={12} className="text-accent fill-accent" />}
                      <div>
                        <div className="font-semibold">{p.title}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">/{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}>{p.status}</span>
                  </td>
                  <td className="px-3 py-3 text-xs">{p.category || "—"}</td>
                  <td className="px-3 py-3 text-xs">{p.author_name || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{p.view_count || 0}</td>
                  <td className="px-3 py-3 text-xs">{p.published_at ? new Date(p.published_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Link to={`/blog/${p.slug}`} target="_blank" data-testid={`blog-view-${p.id}`} className="p-2 text-muted-foreground hover:text-foreground"><ExternalLink size={14} /></Link>
                      <button onClick={() => navigate(`/admin/blog/${p.id}/edit`)} data-testid={`blog-edit-${p.id}`} className="p-2 text-muted-foreground hover:text-foreground"><PencilLine size={14} /></button>
                      <button onClick={() => del(p)} data-testid={`blog-delete-${p.id}`} className="p-2 text-rose-600 hover:text-rose-800"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogList;
